import React, { FC } from 'react';
import { useWindowWidth } from '@investoro/core';
import styled from 'styled-components';
import { isNumber } from 'utils/validators/numbers';

import { OrganizationAndRatingProps } from 'components/common/Card/common/V2CardBodyDefault/Bottom/Bottom';
import { ModificationsProps } from 'components/common/Card/common/V2CardBodyDefault/ModificationsContainer/ModificationsContainer';
import OrganizationContainer from 'components/common/Card/common/V2CardBodyDefault/OrganizationContainer';
import PredicetdPriceAndCalculationContainer from 'components/common/Card/common/V2CardBodyDefault/PredictedPriceAndCalculationContainer';
import Icon, { IconEnum } from 'components/common/Icon';
import LendiButton from 'components/common/Lendi/LendiButton';
import RatingBar from 'components/common/RatingBar/RatingBar';
import { TabType } from 'components/common/Tabs/V2Tabs';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-end;  
  
    @media (max-width: 490px) {
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
    gap: 5px;
    grid-area: ${(props) => props.isUpperLabel ? '3 / right' : '2 / right'};
    text-align: right;
    margin-bottom: 10px;
`;

const RightPart = styled.div`
    margin-left: auto;
`;

const OldPriceValue = styled.h4`
    text-decoration-line: line-through;
    font-weight: 500;
    margin: 0;
    letter-spacing: 1.4px;
    line-height: 20px;
    color: var(--color-primary);
`;

const MainValue = styled.h1`
    font-weight: 600;
    font-size: 19px;
    line-height: inherit;
    color: var(--color-alt);
`;

const Link = styled.a`
    display: flex;
    gap: 5px;
    width: min-content;
    text-decoration: none;
    color: var(--color-primary);
`;

const SubMainValues = styled.div`
    display: flex;
    gap: 5px;
    color: var(--color-primary);
    font-weight: 400;

    b {
        font-weight: 500;
    }
`;

export interface LinkProps {
    name: string;
    url: string;
}

type SubMainValues = (React.ReactNode | string)[]

interface PriceContainerProps {
    isUpperLabel?: boolean;
    oldPrice?: string;
    pricePerSquareMeter?: string;
    deposit?: string;
    category?: string;
    link?: LinkProps;
    modifications?: ModificationsProps;
    mainValue?: string;
    subMainValues?: SubMainValues;
    calculationValue?: string;
    predictedPrice?: string;
    hasCalculationRole?: boolean;
    hasPredictionRole?: boolean;
    openOfferTab?: (tab: TabType) => void;
    organizationAndRating?: OrganizationAndRatingProps;
    ratingScore?: number | null;
    scorePrecision?: number | null;
    showLendiButton?: boolean;
    offerPrice?: number;
}

const PriceContainer: FC<PriceContainerProps> = ({
    isUpperLabel,
    oldPrice,
    link,
    mainValue,
    subMainValues,
    predictedPrice,
    calculationValue,
    openOfferTab,
    organizationAndRating,
    ratingScore,
    scorePrecision,
    hasCalculationRole,
    hasPredictionRole,
    showLendiButton
}) => {
    const preventDefaultHandle = (e: Event) => {
        e.stopPropagation();
    };

    const width = useWindowWidth();

    return (
        <Container>
            {isNumber(ratingScore) && <RatingBar score={ratingScore!} scorePrecision={scorePrecision!} isCardBox/>}
            <RightPart>
                <Wrapper isUpperLabel={isUpperLabel} aria-label="price-container">
                    {oldPrice ? <OldPriceValue role="heading" level={4} aria-label="old-price">{`${oldPrice} zł`} </OldPriceValue> : null}
                    <MainValue role="heading" aria-label="main-value">
                        {mainValue}
                    </MainValue>
                    {subMainValues ? subMainValues.map((value, index) => <SubMainValues role="group" aria-label="additional-data" key={index}>{value}</SubMainValues>) : null}
                    {link
                        ? <Link href={link.url} target="_blank" rel="noopener noreferrer" role="link" aria-label={link.name}
                            onClick={preventDefaultHandle}>
                            <Icon icon={IconEnum.LINK}/>{link.name}
                        </Link>
                        : null}
                    {showLendiButton && <LendiButton />}
                    {width > 489 ? <PredicetdPriceAndCalculationContainer hasPredictionRole={hasPredictionRole} hasCalculationRole={hasCalculationRole} predictedPrice={predictedPrice} calculationValue={calculationValue} openOfferTab={openOfferTab}/> : null}
                </Wrapper>
                {organizationAndRating?.organizationContainer && organizationAndRating.organizationContainer.isReserved !== true ? <OrganizationContainer organizationContainer={organizationAndRating.organizationContainer}/> : null}
            </RightPart>
        </Container>
    );
};

export default PriceContainer;
